import { useEffect, useState } from 'react';
import './App.css';
import './styles/styles.ts';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import {
  LoginPage,
  BatchScreeningPage,
  BusinessLightScreeningPage,
  IndividualLightScreeningPage,
  DigitalWalletLightScreeningPage,
  BatchJobEntitiesPage,
  BatchJobEntityMatchesPage,
  AlertsPage,
  CompanyReportPage,
  CompanyReportDetailPage,
} from './pages';
import { AppContext } from 'src/utils';
import { CleverScreenLayout, LightningScreeningLayout } from './layouts';
import { ToastContainer } from 'react-toastify';
import * as authService from 'src/services/auth.service';
import IUser from '@cc/shared-service/src/server/types/response/IUser';
import MatchFullReportPage from 'src/pages/MatchFullReportPage';
import NetworkAnalystLayout from 'src/layouts/NetworkAnalystLayout';
import NetworkAnalystPage from './pages/NetworkAnalystPage';
import CallbackPage from './pages/CallbackPage';
import * as notify from 'src/services/notification.service';
import * as constants from 'src/constants';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<IUser>();
  const { signOut } = useAuth();

  function logout() {
    authService.deletePersistedCurrentUser();
    setCurrentUser(undefined);
    setIsAuthenticated(false);
    // The returnTo is required due to when having multiple apps
    // It would redirect to the default log out redirect set in WorkOS
    // And we want the logout to redirect to our origin (the full URL of the client)
    signOut({ returnTo: window.location.origin });
  }

  useEffect(() => {
    const currentUser = authService.getCurrentUser();

    if (currentUser) {
      setCurrentUser(currentUser);
      setIsAuthenticated(true);
    } else {
      authService
        .authenticateUser()
        .then((res) => {
          const user = res.data.data.user;
          authService.persistCurrentUser(user);
          setCurrentUser(user);
          setIsAuthenticated(true);
        })
        .catch((error) => {
          if (error.message && error.message === 'User currently not active in the system') {
            notify.error(error.message);
          }

          if (error.response && !error.response.handled) {
            notify.error(constants.TEXT_INVALID_CREDENTIALS);
          }
        });
    }
  }, []);

  return (
    <AppContext.Provider value={{ isAuthenticated, currentUser, logout }}>
      <BrowserRouter>
        {!isAuthenticated ? (
          <Routes>
            <Route path="/" element={<LoginPage />} key="login" />,
            <Route path="*" element={<LoginPage />} key="default-guest-page" />
          </Routes>
        ) : (
          <CleverScreenLayout>
            <Routes>
              <Route path="/callback" element={<CallbackPage />} key="callback" />,
              { currentUser?.screening_disabled ? (
                  <Route path="/" element={<Navigate to="/company-report" />} key="dashboard-redirection" />
                ) : (
                  <Route path="/" element={<Navigate to="/alerts" />} key="dashboard-redirection" />
                )
              },
              <Route path="/company-report" element={<CompanyReportPage />} key="company-report" />,
              <Route
                path="/company-report/detail"
                element={<CompanyReportDetailPage />}
                key="report-entity-matches"
              />,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/lightning-screening/business"
                  element={
                    <LightningScreeningLayout>
                      <BusinessLightScreeningPage />
                    </LightningScreeningLayout>
                  }
                  key="business-light-screening"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/lightning-screening"
                  element={<Navigate to="/lightning-screening/business" />}
                  key="light-screening-redirection"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/lightning-screening/individual"
                  element={
                    <LightningScreeningLayout>
                      <IndividualLightScreeningPage />
                    </LightningScreeningLayout>
                  }
                  key="individual-light-screening"
                />
                ) : null
              }
              ,
              { currentUser?.digital_wallet_visible ? (
                <Route
                  path="/lightning-screening/digital-wallet"
                  element={
                    <LightningScreeningLayout>
                      <DigitalWalletLightScreeningPage />
                    </LightningScreeningLayout>
                  }
                  key="digital-wallet-light-screening"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/batch-screening"
                  element={<BatchScreeningPage />}
                  key="business-batch-screening"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/batch-screening/job-entities/:jobId"
                  element={<BatchJobEntitiesPage />}
                  key="batch-job-entities"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/batch-screening/job-entity-matches/:jobId/:jobEntityId"
                  element={<BatchJobEntityMatchesPage />}
                  key="batch-job-entity-matches"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route
                  path="/match/full-report/:matchId"
                  element={<MatchFullReportPage />}
                  key="match-full-report"
                />
                ) : null
              }
              ,
              { !currentUser?.screening_disabled ? (
                <Route path="/alerts" element={<AlertsPage />} key="match-full-report" />
                ) : null
              }
              ,
              <Route
                path="/network-analyst"
                element={<Navigate to="/network-analyst/business" />}
                key="network-analyst-redirection"
              />
              ,
              <Route
                path="/network-analyst/business"
                element={
                  <NetworkAnalystLayout>
                    <NetworkAnalystPage type='business' />
                  </NetworkAnalystLayout>
                }
                key="business-network-analyst"
              />
              ,
              <Route
                path="/network-analyst/individual"
                element={
                  <NetworkAnalystLayout>
                    <NetworkAnalystPage type='individual' />
                  </NetworkAnalystLayout>
                }
                key="individual-network-analyst"
              />
              ,
              <Route
                path="*"
                element={
                  <div>
                    <h2>404 Page not found</h2>
                  </div>
                }
              />
            </Routes>
          </CleverScreenLayout>
        )}
      </BrowserRouter>
      <ToastContainer />
    </AppContext.Provider>
  );
}

export default App;
